// 由 API 規格書 v1.0 §6 產出，前後端共用契約。
// 改動前請先確認契約已同步更新。

// ── 共用 envelope ─────────────────────────────────────────

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  meta: { executionTime: number; timestamp: string };
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: ApiErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

export type ApiErrorCode =
  | 'INVALID_PAYLOAD'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'INTERNAL_ERROR'
  | 'PAYUNI_ERROR'
  | 'RATE_LIMITED'
  | 'MER_TRADE_NO_DUPLICATE'
  | 'INVALID_FOR_PDF';

// ── 列舉 ─────────────────────────────────────────────────

export type FlowType = 'A' | 'B';

// D1 列舉只有 4 個；'expired' 為 API 層動態映射。
export type DbOrderStatus = 'pending' | 'paid' | 'closed' | 'failed';
export type ApiOrderStatus = DbOrderStatus | 'expired';

export type BankType = '004' | '013' | '822';

export const BANK_TYPE_MAP: Record<BankType, string> = {
  '004': '臺灣銀行',
  '013': '國泰世華銀行',
  '822': '中國信託銀行',
};

// ── 訂單 ─────────────────────────────────────────────────

export interface Order {
  id: string;
  merTradeNo: string;
  tradeNo: string | null;
  flowType: FlowType;
  customerName: string;
  prodDesc: string;
  amount: number;
  bankType: BankType | null;
  bankName: string | null;
  virtualAccount: string | null;
  expireDays: number;
  expireAt: string | null;
  status: ApiOrderStatus;
  linkToken: string | null;
  paymentUrl: string | null;
  linkClicks: number;
  payTime: string | null;
  cancelReason: string | null;
  lastError: string | null;
  lastRefreshedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// ── 各端點 Request / Response ─────────────────────────────

// GET /api/auth/me
export interface AuthMeData {
  email: string;
  displayName: string;
  expiresAt: string;
}

// GET /api/orders
export interface ListOrdersQuery {
  status?: ApiOrderStatus | 'all';
  flow?: FlowType;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ListOrdersData {
  items: Order[];
  pagination: Pagination;
}

// POST /api/orders/flow-a
export interface CreateFlowAPayload {
  customerName: string;
  prodDesc: string;
  amount: number;
  expireDays: number;
  bankType: BankType;
}

export interface CreateFlowAData {
  id: string;
  merTradeNo: string;
  tradeNo: string;
  virtualAccount: string;
  bankType: BankType;
  bankName: string;
  amount: number;
  expireAt: string;
  status: 'pending';
  receiptUrl: string;
}

// POST /api/orders/flow-b
export interface CreateFlowBPayload {
  customerName: string;
  prodDesc: string;
  amount: number;
  expireDays: number;
}

export interface CreateFlowBData {
  id: string;
  merTradeNo: string;
  amount: number;
  expireAt: string;
  status: 'pending';
  linkToken: string;
  paymentUrl: string;
}

// POST /api/orders/:id/close
export interface CloseOrderPayload {
  cancelReason: string;
}

export interface CloseOrderData {
  id: string;
  status: 'closed';
  cancelReason: string;
  updatedAt: string;
}

// RATE_LIMITED 錯誤的 details
export interface RateLimitedDetails {
  retryAfterSeconds: number;
}
